import { t } from './lib/i18n';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import Layout from './components/Layout';
import { Spinner } from './components/ui';
import { useApp } from './lib/app-context';
import Landing from './pages/Landing';
import Search from './pages/Search';
import PhotoCredits from './pages/PhotoCredits';
import Listing from './pages/Listing';
import BookingFlow from './pages/BookingFlow';
import BookingDetail from './pages/BookingDetail';
import Auth from './pages/Auth';
import Account from './pages/Account';
import Messages from './pages/Messages';
import ColocationFeed from './pages/ColocationFeed';
import ColocationForm from './pages/ColocationForm';
import HostDashboard from './pages/HostDashboard';
import PropertyWizard from './pages/PropertyWizard';
import HostListing from './pages/HostListing';
import Kyc from './pages/Kyc';
import Admin from './pages/Admin';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile';
import BecomeHost from './pages/BecomeHost';

/**
 * Routes. French paths, because the product is French-first and the URL is part of
 * the interface.
 */

function Guard({ children, role }: { children: ReactNode; role?: 'HOST' | 'ADMIN' }) {
  const { user, loadingSession, isHost, isAdmin } = useApp();
  const location = useLocation();

  if (loadingSession) {
    return (
      <div className="page center" style={{ paddingTop: 80 }}>
        <Spinner label={t("Chargement de votre session…")} />
      </div>
    );
  }
  if (!user) {
    // Remember where they were headed, so signing in lands them there.
    return <Navigate to="/connexion" replace state={{ from: location.pathname + location.search }} />;
  }
  if (role === 'HOST' && !isHost) return <Navigate to="/devenir-hote" replace />;
  if (role === 'ADMIN' && !isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/recherche" element={<Search />} />
        <Route path="/logement/:id" element={<Listing />} />
        <Route path="/profil/:id" element={<Profile />} />
        <Route path="/colocations" element={<ColocationFeed />} />
        <Route path="/credits-photos" element={<PhotoCredits />} />

        <Route path="/connexion" element={<Auth mode="login" />} />
        <Route path="/inscription" element={<Auth mode="signup" />} />
        <Route path="/mot-de-passe" element={<Auth mode="reset" />} />

        <Route
          path="/reserver/:id"
          element={
            <Guard>
              <BookingFlow />
            </Guard>
          }
        />
        <Route
          path="/reservations/:id"
          element={
            <Guard>
              <BookingDetail />
            </Guard>
          }
        />
        <Route
          path="/compte"
          element={
            <Guard>
              <Account />
            </Guard>
          }
        />
        <Route
          path="/messages"
          element={
            <Guard>
              <Messages />
            </Guard>
          }
        />
        <Route
          path="/messages/:conversationId"
          element={
            <Guard>
              <Messages />
            </Guard>
          }
        />
        <Route
          path="/notifications"
          element={
            <Guard>
              <Notifications />
            </Guard>
          }
        />
        <Route
          path="/parametres"
          element={
            <Guard>
              <Settings />
            </Guard>
          }
        />
        <Route
          path="/verification"
          element={
            <Guard>
              <Kyc />
            </Guard>
          }
        />
        <Route
          path="/devenir-hote"
          element={
            <Guard>
              <BecomeHost />
            </Guard>
          }
        />
        <Route
          path="/colocations/publier"
          element={
            <Guard>
              <ColocationForm />
            </Guard>
          }
        />
        <Route
          path="/colocations/:id/modifier"
          element={
            <Guard>
              <ColocationForm />
            </Guard>
          }
        />

        <Route
          path="/hote"
          element={
            <Guard role="HOST">
              <HostDashboard />
            </Guard>
          }
        />
        <Route
          path="/hote/annonces/nouvelle"
          element={
            <Guard role="HOST">
              <PropertyWizard />
            </Guard>
          }
        />
        <Route
          path="/hote/annonces/:id"
          element={
            <Guard role="HOST">
              <HostListing />
            </Guard>
          }
        />

        <Route
          path="/admin"
          element={
            <Guard role="ADMIN">
              <Admin />
            </Guard>
          }
        />

        <Route
          path="*"
          element={
            <div className="page center" style={{ paddingTop: 80 }}>
              <h1>{t("Page introuvable")}</h1>
              <p className="muted">{t("Le lien que vous avez suivi ne mène nulle part.")}</p>
              <a className="btn" href="/">
                {t("Retour à l’accueil")}
              </a>
            </div>
          }
        />
      </Route>
    </Routes>
  );
}
